/**
 * Mic capture engine (implementation-plan.md §1.1 capture layer + §1.3b).
 *
 * Two modes, exactly as the plan specifies:
 *   open mic        — continuous recording while the session is live; the recorder emits
 *                     fixed-size chunks, each one an anchored segment
 *   push-to-talk    — each mic toggle is one anchored segment
 *
 * Every segment carries `{ chunkStartWall, durationMs, bytes }` (plan §1.3a step 2) and is
 * pre-processed to 16 kHz mono WAV before it is handed to the uploader, so the sync engine
 * and the transcription proxy both get what they expect.
 */
import { closeMic, MicError, openMic, type MicFailure, type MicStream } from './mic';
import { chunkPlan, isDecodeFailure, preprocessRecording, webmInitSegment } from './preprocess';
import { nowWallMs } from '../sync/engine';
import type { AudioChunkRecord } from '../api/client';

export type MicMode = 'open' | 'push-to-talk';
export type MicCaptureState = 'idle' | 'requesting' | 'recording' | 'paused' | 'stopped' | 'error';

export interface CaptureSegment {
  segmentIndex: number;
  /** Wall clock (performance.now()) at which this segment's audio started. */
  startWallMs: number;
  /** Wall-clock length of the segment. */
  durationMs: number;
  /** Encoded size of the raw recording (pre-processed chunks are listed separately). */
  rawBytes: number;
  /** Uploaded WAV chunks (a long segment splits into several). */
  chunks: AudioChunkRecord[];
  peakDbfs: number | null;
  /** Video window resolved through the sync engine, when one was supplied. */
  video: { startS: number; endS: number } | null;
}

export interface MicCaptureOptions {
  mode: MicMode;
  /** Open-mic chunk length. Defaults to 30 s (well inside the API's 25-minute limit). */
  chunkMs?: number;
  /** Receives one pre-processed WAV chunk. Wired to POST /api/sessions/:id/audio. */
  upload: (
    wav: Blob,
    meta: { chunkIndex: number; chunkStartWallMs: number; durationMs: number },
  ) => Promise<AudioChunkRecord>;
  /** Maps a wall instant onto the video timeline (the sync engine). */
  mapWallToVideo?: (wallMs: number) => number | null;
  /** Anchors a completed segment through the sync engine. */
  anchorSegment?: (startWallMs: number, endWallMs: number) => void;
  onSegment?: (segment: CaptureSegment) => void;
  onError?: (error: MicError) => void;
  onStateChange?: (state: MicCaptureState) => void;
  /** Plan §6.1: iOS Safari drops the mic on tab blur — auto-pause the session. */
  pauseOnHidden?: boolean;
}

export class MicCapture {
  private readonly options: MicCaptureOptions;
  private readonly chunkMs: number;
  private mic: MicStream | null = null;
  private recorder: MediaRecorder | null = null;
  private state: MicCaptureState = 'idle';
  private segments: CaptureSegment[] = [];
  private pending: Promise<void> = Promise.resolve();
  private activeSegment: CaptureSegment | null = null;
  private activeParts: Blob[] = [];
  /** WebM init segment (EBML header + Tracks) captured from the first timeslice. */
  private initSegment: Uint8Array<ArrayBuffer> | null = null;
  /**
   * True once a mid-stream timeslice could not be decoded standalone (non-WebM containers).
   * From then on the rest of the segment is decoded in one piece at segment end — no audio is
   * dropped, the live transcript just lags for that browser.
   */
  private deferred = false;
  private deferredBlobs: Blob[] = [];
  private lastChunkWall = 0;
  private uploadIndex = 0;
  private visibilityHandler: (() => void) | null = null;

  constructor(options: MicCaptureOptions) {
    this.options = options;
    this.chunkMs = options.chunkMs ?? 30_000;
  }

  get currentState(): MicCaptureState {
    return this.state;
  }

  get isRecording(): boolean {
    return this.state === 'recording';
  }

  get capturedSegments(): readonly CaptureSegment[] {
    return this.segments;
  }

  get micLabel(): string {
    return this.mic?.trackLabel ?? '';
  }

  get mimeType(): string | null {
    return this.mic?.mimeType ?? null;
  }

  /** Opens the microphone and, for open-mic mode, starts recording immediately. */
  async start(): Promise<void> {
    if (this.state === 'recording' || this.state === 'paused') return;
    this.setState('requesting');
    try {
      this.mic = await openMic();
    } catch (error) {
      this.setState('error');
      const micError = error instanceof MicError ? error : new MicError('unknown', String(error), error);
      this.options.onError?.(micError);
      throw micError;
    }

    if (this.options.pauseOnHidden !== false) this.watchVisibility();
    if (this.options.mode === 'open') this.beginSegment();
    else this.setState('idle');
  }

  /** Push-to-talk: one toggle starts a segment, the next ends it. */
  async toggle(): Promise<CaptureSegment | null> {
    if (this.state === 'recording') return this.endSegment();
    if (this.state === 'idle' || this.state === 'paused') {
      this.beginSegment();
      return null;
    }
    return null;
  }

  /** Stops recording, flushes the final segment and releases the microphone. */
  async stop(): Promise<CaptureSegment[]> {
    if (this.state === 'recording') await this.endSegment();
    await this.pending;
    this.unwatchVisibility();
    closeMic(this.mic?.stream ?? null);
    this.mic = null;
    this.recorder = null;
    this.setState('stopped');
    return this.segments;
  }

  /** Auto-pause (tab blur, or the coach pressing pause). */
  async pause(): Promise<void> {
    if (this.state !== 'recording') return;
    await this.endSegment();
    this.setState('paused');
  }

  async resume(): Promise<void> {
    if (this.state !== 'paused') return;
    if (this.options.mode === 'open') this.beginSegment();
    else this.setState('idle');
  }

  private beginSegment(): CaptureSegment {
    if (!this.mic) throw new MicError('unknown', 'Microphone is not open');
    const { stream, mimeType } = this.mic;
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

    const segment: CaptureSegment = {
      segmentIndex: this.segments.length,
      startWallMs: nowWallMs(),
      durationMs: 0,
      rawBytes: 0,
      chunks: [],
      peakDbfs: null,
      video: null,
    };
    this.segments.push(segment);
    this.activeSegment = segment;
    this.activeParts = [];
    this.deferredBlobs = [];
    this.initSegment = null;
    this.deferred = false;
    this.lastChunkWall = segment.startWallMs;

    recorder.addEventListener('dataavailable', (event) => {
      if (!event.data || event.data.size === 0) return;
      this.activeParts.push(event.data);
      segment.rawBytes += event.data.size;

      // First timeslice carries the container init (EBML header / Tracks) — keep it so later
      // timeslices, which have none, can be decoded on their own.
      if (this.initSegment === null && this.options.mode === 'open') {
        void event.data.arrayBuffer().then((buffer) => {
          this.initSegment = webmInitSegment(new Uint8Array(buffer));
        });
      }

      // Open mic: every timeslice IS one anchored segment, uploaded as it lands.
      if (this.options.mode === 'open' && !this.deferred) {
        const chunkEnd = nowWallMs();
        const blob = new Blob(this.activeParts.splice(0, this.activeParts.length), {
          type: mimeType ?? 'audio/webm',
        });
        const startWall = this.lastChunkWall;
        this.lastChunkWall = chunkEnd;
        this.enqueue(blob, startWall, chunkEnd - startWall, segment);
      }
    });

    recorder.addEventListener('error', (event) => {
      this.options.onError?.(
        new MicError('unknown', `Recorder error: ${String((event as ErrorEvent).error ?? 'unknown')}`),
      );
    });

    this.recorder = recorder;
    if (this.options.mode === 'open') recorder.start(this.chunkMs);
    else recorder.start();
    this.setState('recording');
    return segment;
  }

  private async endSegment(): Promise<CaptureSegment | null> {
    const recorder = this.recorder;
    const segment = this.activeSegment;
    if (!recorder || !segment) return null;
    const endWall = nowWallMs();

    await new Promise<void>((resolve) => {
      if (recorder.state === 'inactive') {
        resolve();
        return;
      }
      recorder.addEventListener('stop', () => resolve(), { once: true });
      recorder.stop();
    });

    // Push-to-talk keeps its audio in one part: upload it as a single anchored segment.
    if (this.options.mode === 'push-to-talk' && this.activeParts.length > 0) {
      const blob = new Blob(this.activeParts.splice(0, this.activeParts.length), {
        type: this.mic?.mimeType ?? 'audio/webm',
      });
      this.enqueue(blob, segment.startWallMs, Math.max(1, endWall - segment.startWallMs), segment, null);
    }

    // Deferred (non-WebM) segments are decoded in one piece at the end, header first.
    if (this.deferredBlobs.length > 0) {
      const whole = new Blob(
        [...(this.initSegment ? [this.initSegment] : []), ...this.deferredBlobs.splice(0)],
        { type: this.mic?.mimeType ?? 'audio/webm' },
      );
      this.enqueue(whole, segment.startWallMs, Math.max(1, endWall - segment.startWallMs), segment, null);
    }

    segment.durationMs = Math.max(segment.durationMs, endWall - segment.startWallMs);
    this.recorder = null;
    this.activeSegment = null;
    // Push-to-talk goes back to idle so the next toggle opens a new segment; open mic is
    // paused until the coach resumes (or the page comes back into focus).
    this.setState(this.options.mode === 'push-to-talk' ? 'idle' : 'paused');
    await this.pending;
    return segment;
  }

  /**
   * Pre-processes one raw blob into 16 kHz mono WAV, splits it to respect the transcription
   * API's 25 MB / 25 min limits, uploads every chunk, then updates the segment.
   *
   * `containerHeader` is prepended to mid-stream timeslices: a MediaRecorder timeslice after
   * the first one carries no container init (no EBML header / Tracks), so `decodeAudioData`
   * cannot read it standalone. Prepending the init segment captured from the first timeslice
   * makes each chunk decodable on its own, which is what keeps the live transcript live.
   */
  private enqueue(
    blob: Blob,
    startWallMs: number,
    durationMs: number,
    segment: CaptureSegment,
    containerHeader: Uint8Array<ArrayBuffer> | null = this.initSegment,
  ): void {
    this.pending = this.pending.then(async () => {
      try {
        let payload = blob;
        if (containerHeader && containerHeader.byteLength > 0) {
          payload = new Blob([containerHeader, blob], { type: blob.type || 'audio/webm' });
        }
        const processed = await preprocessRecording(payload);
        const wav = new Blob([processed.wav], { type: 'audio/wav' });
        const plan = chunkPlan({
          totalBytes: processed.wav.byteLength,
          totalDurationMs: processed.durationMs,
        });

        for (const spec of plan) {
          const slice = wav.slice(spec.startByte, spec.endByte, 'audio/wav');
          const record = await this.options.upload(slice, {
            chunkIndex: this.uploadIndex,
            chunkStartWallMs: startWallMs + spec.startWallOffsetMs,
            durationMs: spec.durationMs,
          });
          segment.chunks.push(record);
          this.uploadIndex += 1;
        }

        segment.peakDbfs = processed.peakDbfs;
        segment.durationMs = Math.max(segment.durationMs, startWallMs + durationMs - segment.startWallMs);
        segment.video =
          this.options.mapWallToVideo && this.options.anchorSegment
            ? (() => {
                this.options.anchorSegment!(startWallMs, startWallMs + durationMs);
                const startS = this.options.mapWallToVideo!(startWallMs);
                const endS = this.options.mapWallToVideo!(startWallMs + durationMs);
                return startS === null || endS === null ? null : { startS, endS };
              })()
            : null;
        this.options.onSegment?.(segment);
      } catch (error) {
        // A mid-stream timeslice with no container init cannot be decoded on its own. Rather
        // than lose the audio, switch this segment to one-piece processing at segment end.
        if (this.options.mode === 'open' && !this.deferred && isDecodeFailure(error)) {
          this.deferred = true;
          this.deferredBlobs = [blob, ...this.activeParts.splice(0, this.activeParts.length)];
          this.options.onError?.(
            new MicError(
              'unknown',
              'This browser cannot decode recorder chunks mid-stream — the segment will be transcribed as a whole when it ends.',
              error,
            ),
          );
          return;
        }
        this.options.onError?.(
          error instanceof MicError
            ? error
            : new MicError('unknown', `Could not process an audio segment: ${String(error)}`, error),
        );
      }
    });
  }

  private watchVisibility(): void {
    if (typeof document === 'undefined' || this.visibilityHandler) return;
    this.visibilityHandler = () => {
      if (document.visibilityState === 'hidden' && this.state === 'recording') void this.pause();
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private unwatchVisibility(): void {
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    this.visibilityHandler = null;
  }

  private setState(state: MicCaptureState): void {
    if (this.state === state) return;
    this.state = state;
    this.options.onStateChange?.(state);
  }
}

export type { MicFailure };
